import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, filter } from 'rxjs';
import { REMOTE_CLIENTS_COLUMNS, REMOTE_CLIENTS_TABLE_HEADER, REMOTE_CLIENTS_PAGE_SIZE_OPTIONS, REMOTE_CLIENTS_PAGE_DEFAULT_SIZE } from 'src/app/conf/remote-clients';
import { Device } from 'src/app/model/device';
import { RemoteClient, RemoteClients } from 'src/app/model/remote-client';
import * as DeviceActions from 'src/app/store/device/device.actions';

import { AppState } from 'src/app/store/app.reducer';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { selectCurrentDevice, selectCurrentDeviceRemoteClients, selectRemoteClientDeleted, selectRemoteClientDeleting } from 'src/app/store/device/device.selector';
import { TranslateService } from '@ngx-translate/core';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectUser } from 'src/app/store/user/user.selector';
import { User } from 'src/app/model/user';


@Component({
	selector: 'app-remote-devices',
	templateUrl: './remote-devices.component.html',
	providers: [ConfirmationService, DialogService]
})
export class RemoteDevicesComponent implements OnInit, OnDestroy {
	device$: Observable<Device | undefined>;
	deviceSub: Subscription | undefined;
	remoteClients$: Observable<RemoteClients | undefined>;
	remoteClientsSub: Subscription | undefined;
	currentUser: User;
	currentUser$: Observable<User | undefined>;
	currentUserSub: Subscription | undefined;

	displayedColumns = REMOTE_CLIENTS_TABLE_HEADER;
	pageSize = REMOTE_CLIENTS_PAGE_DEFAULT_SIZE;

	device: Device;

	remoteClientDeleted: boolean;
	remoteClientDeleted$: Observable<boolean | undefined>;
	remoteClientDeletedSub: Subscription | undefined;

	remoteClientDeleting: boolean;
	remoteClientDeleting$: Observable<boolean | undefined>;
	remoteClientDeletingSub: Subscription;

	claimedSlots: RemoteClient[] = [];
	maxSlots = 0;

	totalRecords = 0;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	// Design
	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public confirmationService: ConfirmationService,
	)  {
		this.initSelectors();
	}

	initSelectors(): void{
		// Store selectors
		this.device$ = this.store.select(selectCurrentDevice);
		this.remoteClients$ = this.store.select(selectCurrentDeviceRemoteClients);
		this.currentUser$ = this.store.select(selectUser);
		this.remoteClientDeleted$ = this.store.select(selectRemoteClientDeleted);
		this.remoteClientDeleting$ = this.store.select(selectRemoteClientDeleting);
	}

	ngOnInit(): void {
		this.store.dispatch(DeviceActions.getRemoteClients({}));
		this.subscribeAll();
		this.onResize();
	}

	subscribeAll(): void {
		this.deviceSub = this.device$?.subscribe(
			(device) => {
				this.device = device;
			}
		);
		this.remoteClientsSub = this.remoteClients$.pipe(filter(rc => !!rc))
			.subscribe(remoteClientsData => {
				this.claimedSlots = remoteClientsData.ClaimedSlots;
				this.maxSlots = remoteClientsData.MaxSlots;
				this.totalRecords = remoteClientsData.ClaimedSlots.length;
			}
		);
		this.currentUserSub = this.currentUser$?.subscribe(
			(user) => {
				this.currentUser = user;
			}
		);
		this.remoteClientDeletedSub = this.remoteClientDeleted$.pipe(filter(d=> !!d)).subscribe(() => {
			this.store.dispatch(DeviceActions.resetFinalUserRemoteClients());
		});
		this.remoteClientDeletingSub = this.remoteClientDeleting$.subscribe(
			(remoteClientDeleting) => {
				this.remoteClientDeleting = remoteClientDeleting;
			}
		);
	}

	ngOnDestroy(): void {
		this.deviceSub?.unsubscribe();
		this.remoteClientsSub?.unsubscribe();
		this.currentUserSub?.unsubscribe();
		this.remoteClientDeletedSub?.unsubscribe();
		this.remoteClientDeletingSub?.unsubscribe();
	}

	deleteClient(client: RemoteClient): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_REMOTE_CLIENT_AUTOMATION_HEADER'),
			message: this.translate.instant('OWNER_DELETE_REMOTE_CLIENT_AUTOMATION_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
				accept: () => {
					this.store.dispatch(DeviceActions.deleteRemoteClient({ slotId: client.Id }));
			},
			reject: () => {
			}
		});
	}

	toggleClientLock(client: RemoteClient): void {
		this.store.dispatch(DeviceActions.toggleRemoteClientLock({ slotId: client.Id, locked: !client.Locked }));
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}
}